// Display units (Round 4). Everything is stored and transmitted in aviation units — feet, knots —
// because that is what ADS-B reports and what the backend reasons in. Metric is a *display*
// conversion applied here, at the edge, and converted back at inputs. Distance is km in both modes.

export type Units = "aviation" | "metric";

const FT_TO_M = 0.3048;
const KT_TO_KMH = 1.852;

const n = (v: number) => v.toLocaleString("en-GB", { maximumFractionDigits: 0 });

export function formatAltitude(ft: number, units: Units): string {
  return units === "metric" ? `${n(Math.round((ft * FT_TO_M) / 10) * 10)} m` : `${n(ft)} ft`;
}

export function formatSpeed(kt: number, units: Units): string {
  return units === "metric" ? `${n(kt * KT_TO_KMH)} km/h` : `${n(kt)} kt`;
}

export const altitudeUnit = (units: Units) => (units === "metric" ? "m" : "ft");

/** Rule editor: stored feet → the number shown in the input. Metric snaps to 100 m, so a value typed
 *  in metres survives the round trip through feet without drifting (1,500 m → 4,921 ft → 1,500 m). */
export function feetToInput(ft: number, units: Units): number {
  return units === "metric" ? Math.round((ft * FT_TO_M) / 100) * 100 : ft;
}

export function inputToFeet(value: number, units: Units): number {
  return units === "metric" ? Math.round(value / FT_TO_M) : value;
}

export const altitudeStep = (units: Units) => (units === "metric" ? 100 : 500);
export const altitudeMax = (units: Units) => (units === "metric" ? 18_000 : 60_000);

/** Legend threshold labels, compact: "2k" ft or "0.6k" m. */
export function legendLabel(ft: number, units: Units): string {
  const value = units === "metric" ? ft * FT_TO_M : ft;
  if (value >= 10_000) return `${Math.round(value / 1000)}k`;
  if (value >= 1000) return `${(value / 1000).toFixed(units === "metric" ? 1 : 0)}k`;
  return units === "metric" ? `${(value / 1000).toFixed(1)}k` : `${Math.round(value)}`;
}
